import { Panel } from '@assets/component/Panel/Panel';
import { PanelHeader } from '@assets/component/Panel/PanelHeader/PanelHeader';
import {
    PanelColumn,
    PanelRow,
    PanelTable
} from '@assets/component/Panel/PanelTable/PanelTable';
import { GetSectionIconUrl } from '@assets/component/SectionIcons/SectionIcon';
import { Flex } from '@assets/component/UI/Flex/Flex';
import { useStore } from '@assets/model/Store';
import { max, min } from 'lodash';
import {
    formatDollar,
    formatPercent,
    formatPercentNoMultiply
} from '../../PanelElements/Formatters';
import { RowLabel } from '../../PanelElements/RowLable/RowLable';
import { RowValue } from '../../PanelElements/RowValue/RowValue';
import * as style from './PanelCustomers.module.less';
import _ from 'lodash';
import { SchemaFieldKeys } from '@assets/model/FieldsSchema';
import { onEditReportField } from '../../UpdateFieldsUtil';
import { observer } from 'mobx-react';
export const PanelCustomers = observer(() => {
    const store = useStore();
    const query = useStore().query;

    const getCustomersTable = () => {
        const customers = query.top_customers;
        if (!customers) {
            return null;
        }

        const colPercent: Partial<PanelColumn> = {
            element: RowValue.percent,
            overrideFormatting: formatPercent
        };

        const rowDefaults: Partial<PanelRow> = {
            element: RowValue.value,
            label: RowLabel.label,
            formatter: data => formatDollar(data.toString())
        };

        const getYearRange = () => {
            const years = [];
            for (const customer of customers) {
                for (const k in customer.revenue) {
                    const e = customer.revenue[k];
                    if (e.year > 0 && years.includes(e.year) === false) {
                        years.push(e.year);
                    }
                }
            }

            return { minYear: min(years), maxYear: max(years) };
        };
        const ALL_YEAR_CUSTOMER_INDEX = -1;
        const { minYear, maxYear } = getYearRange();
        const customerOrder = customers
            .map((c, index) => ({ name: c.name, index }))
            .sort((a, b) => {
                const aRev = customers[a.index]['revenue'].find(
                    e => e.year === maxYear
                )?.revenue;
                const bRev = customers[b.index]['revenue'].find(
                    e => e.year === maxYear
                )?.revenue;
                if (aRev === undefined || bRev === undefined) {
                    return 0;
                }
                return bRev - aRev; // Sort by revenue in descending order
            })
            .map(c => c.index);

        customerOrder.push(ALL_YEAR_CUSTOMER_INDEX);

        const cols_customers: PanelColumn[] = [];
        for (let i = minYear; i <= maxYear; i++) {
            cols_customers.push({
                column: { source: i.toString(), display: i.toString() }
            });
            cols_customers.push({
                column: {
                    source: i.toString(),
                    display: '%'
                },
                overrideValue: (field, column) => {
                    const year = parseInt(column.source);
                    const customerIndex = parseInt(field.source);
                    var rev = getCustomerRevenue(customerIndex, year);
                    var totalRev = query.valueByYear(
                        'net_revenue_per_year',
                        year
                    );

                    if (totalRev == 'N/A' || rev == 0) {
                        return '-';
                    }

                    return rev / totalRev;
                },
                ...colPercent
            });
        }

        cols_customers.push({
            header: col => {
                return (
                    <Flex fillCell={true} justify="start">
                        {col.column.display}
                    </Flex>
                );
            },
            column: { source: null, display: 'Source' },
            element: RowValue.sources,
            dontUseRowFormatting: true,
            overrideValue: (field, column) => {
                const customerIndex = parseInt(field.source);
                const customer = customers[customerIndex];
                if (!customer) {
                    return [];
                }

                return customer.page_sources;
            }
        });

        const getCustomerRevenue = (customerIndex: number, year: number) => {
            if (customerIndex === ALL_YEAR_CUSTOMER_INDEX) {
                return _(customers)
                    .flatMap(c => c.revenue)
                    .filter(e => e.year === year)
                    .reduce((acc, e) => acc + (e.revenue || 0), 0);
            }
            const customer = customers[customerIndex];
            if (!customer) {
                return 0;
            }

            return customer.revenue.find(e => e.year === year)?.revenue ?? 0;
        };

        const rows_customers: PanelRow[] = [];
        for (let i in customerOrder) {
            const customerIndex = customerOrder[i];
            const customerName =
                customerIndex == ALL_YEAR_CUSTOMER_INDEX
                    ? 'Totals'
                    : customers[customerIndex].name;

            const row: PanelRow = {
                field: {
                    source: customerIndex.toString(),
                    display: customerName
                },
                value: (field, column) => {
                    const year = parseInt(column.source);
                    const customerIndex = parseInt(field.source);
                    var rev = getCustomerRevenue(customerIndex, year);
                    if (rev == 0) {
                        return '-';
                    }
                    return rev;
                },
                ...rowDefaults
            };
            rows_customers.push(row);
        }

        return (
            <PanelTable
                className={style.panelTableStyle}
                topLeftCellText="($ in Millions)"
                columns={cols_customers}
                rows={rows_customers}
                disable_round_edges={true}
                onEditField={(row, column, newValue, rowIndex) => {
                    console.log('Edit field', row, column, newValue, rowIndex);
                }}
            />
        );
    };

    const getTopCustomersRevenueGroup = () => {
        const colPercent: Partial<PanelColumn> = {
            element: RowValue.percent,
            overrideFormatting: formatPercentNoMultiply
        };

        const rowDefaults: Partial<PanelRow> = {
            element: RowValue.value,
            label: RowLabel.label,
            formatter: data => formatDollar(data.toString()),
            editable: true
        };

        const getYearRange = () => {
            const reportYear = query.actualYear() || new Date().getFullYear();
            return { minYear: reportYear - 2, maxYear: reportYear + 1 };
        };
        const { minYear, maxYear } = getYearRange();

        const cols_top_rev_by_year: PanelColumn[] = [];
        for (let i = minYear; i <= maxYear; i++) {
            cols_top_rev_by_year.push({
                column: {
                    source: i.toString(),
                    display: `${i}`
                },
                ...colPercent
            });
        }

        cols_top_rev_by_year.push({
            header: col => {
                return (
                    <Flex fillCell={true} justify="start">
                        {col.column.display}
                    </Flex>
                );
            },
            column: { source: null, display: 'Source' },
            element: RowValue.sources,
            dontUseRowFormatting: true,
            overrideValue: (field, column) => {
                const groupSources = query.sources(field, column);
                if (groupSources.length === 0) {
                    return _.uniq(
                        query.top_customers.flatMap(c => c.page_sources)
                    );
                }

                return groupSources;
            }
        });

        const getTopCustomerRevenueShare = (customersCount: number) => {
            return (field, column) => {
                const year = parseInt(column.source);
                const val = query.valueByYear(
                    field.source as SchemaFieldKeys,
                    year
                );

                if (val === 'N/A') {
                    //If not available we try to calc based on customers
                    if (query.top_customers) {
                        const totalRevTopCustomers = query.top_customers
                            .flatMap(c => c.revenue)
                            .filter(e => e.year === year)
                            .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
                            .slice(0, customersCount);

                        if (totalRevTopCustomers.find(e => e.revenue === 0)) {
                            return 'N/A';
                        }

                        const isMinimumCustomersCount =
                            totalRevTopCustomers.length >= customersCount;

                        if (!isMinimumCustomersCount) {
                            return 'N/A';
                        }

                        const finalTotalRevenue = totalRevTopCustomers.reduce(
                            (acc, e) => acc + (e.revenue || 0),
                            0
                        );

                        const totalRev = query.valueByYear(
                            'net_revenue_per_year',
                            year
                        );

                        if (
                            totalRev === 'N/A' ||
                            totalRev === 0 ||
                            finalTotalRevenue === 0
                        ) {
                            return 'N/A';
                        }

                        return ((finalTotalRevenue / totalRev) * 100).toFixed(
                            2
                        );
                    }
                }

                return val;
            };
        };
        const rows_top_10_customers: PanelRow[] = [
            {
                field: {
                    source: 'top_1_customers_revenue_percentage',
                    display: 'Top 1 Customers Revenue %'
                },
                value: getTopCustomerRevenueShare(1),
                ...rowDefaults
            },
            {
                field: {
                    source: 'top_10_customers_revenue_percentage',
                    display: 'Top 10 Customers Revenue %'
                },
                value: getTopCustomerRevenueShare(10),
                ...rowDefaults
            }
        ];

        return (
            <PanelTable
                className={style.panelTableStyle}
                topLeftCellText=""
                columns={cols_top_rev_by_year}
                rows={rows_top_10_customers}
                onEditField={onEditReportField}
            />
        );
    };
    const customersTable = getCustomersTable();
    const topCustomersTable = getTopCustomersRevenueGroup();

    const icon = GetSectionIconUrl('concentration');
    return (
        <>
            <Panel>
                <PanelHeader title="Customers" iconUrl={icon} />
                {store.showAdminConsole && customersTable}
                {topCustomersTable}
            </Panel>
        </>
    );
});
